import { DEFAULT_POLICY, WIX_EU_POLICY, TranslateFn } from './types'
import {
  byHook,
  render,
  logger,
  getLanguage,
  getStaticsUrl,
  shouldUseCipaBanner,
} from './utils'
import { init as initTranslations } from './i18n'
import { BANNER_HOOK } from './components/banner/banner.template'
import {
  withRootTemplate,
  createModalTemplate,
  createBannerTemplate,
  createCipaBannerTemplate,
  MODAL_SAVE_HOOK,
  MODAL_CLOSE_HOOK,
  MODAL_RECOMMENDED_CHECKBOX_HOOK,
  BANNER_ACCEPT_HOOK,
  BANNER_DECLINE_HOOK,
  BANNER_SETTINGS_HOOK,
  BANNER_DECLINE_ALL_HOOK,
  MODAL_HOOK,
  LINK_HOOK,
} from './components'
import { DataCapsule, LocalStorageStrategy } from '@wix/data-capsule';
import classes from './components/root/root.css'

export const BANNER_WRAPPER_HOOK = 'banner-wrapper'
export const MODAL_WRAPPER_HOOK = 'modal-wrapper'
export const BANNER_WRAPPER_DATA_COMP_TYPE = 'cookie-banner-root-wix'
export const MODAL_WRAPPER_DATA_COMP_TYPE = 'cookie-banner-modal-wix'
const CIPA_BANNER_CLOSED_FLAG = 'cipa-banner-closed'

const noop = () => null

/**
 * `fontsLoading` and `fontsFallback` are changed in `onFontLoaded`,
 * which is executed as `callback` in ~/utils/font-loader.ts
 * This needed for:
 * * remove `hidden` class name from root, because banner is hidden by default
 * * add `fontFallback` class name, if fonts are not loaded
 */

let fontsLoading = true
let fontsFallback = false

const rootClassName = (fallback: boolean, hidden: boolean) => {
  let className = classes.root
  if (fallback) {
    className += ` ${classes.fontFallback}`
  }
  if (hidden) {
    className += ` ${classes.hidden}`
  }

  return className
}

const focusBanner = () => {
  setTimeout(() => {
    const bannerContainer = document.body.querySelector<HTMLInputElement>(byHook(BANNER_HOOK))
    if (bannerContainer) {
      bannerContainer.focus()
    }
  }, 1000)
}

const focusBannerOnRender = () => {
  const callback = (mutationsList: any) => {
    for (let mutation of mutationsList) {
      if (mutation.type === 'childList') {
        focusBanner()
        observer.disconnect();
      }
    }
  };
  const config = { childList: true, subtree: true };
  const observer = new MutationObserver(callback);
  observer.observe(document.body, config);
}

const getDataCapsuleData = async (dataCapsule: DataCapsule) => {
  try {
    const cipaBannerClosed = await dataCapsule.getItem(CIPA_BANNER_CLOSED_FLAG)
    return cipaBannerClosed
  } catch (e) {
    return undefined
  }
}

const renderBanner = async (
  t: TranslateFn,
  showClose: boolean = true,
  showDeclineAll: boolean = false,
) => {
  const consentPolicyManager = (window as any).consentPolicyManager
  const { defaultPolicy } = consentPolicyManager.getCurrentConsentPolicy()
  const useCipaBanner = shouldUseCipaBanner()
  let dataCapsule: DataCapsule | undefined

  if (!defaultPolicy) {
    return
  }
  if (useCipaBanner) {
    dataCapsule = new DataCapsule({
      strategy: new LocalStorageStrategy(),
      namespace: 'cookie-consent-settings-ui'
    });
    const cipaBannerClosed = await getDataCapsuleData(dataCapsule)

    if (cipaBannerClosed) {
      return;
    }
  }

  logger.rendered()

  const bannerTemplate = useCipaBanner
    ? createCipaBannerTemplate(t, showClose)
    : createBannerTemplate(t, showClose, showDeclineAll)

  const root = render(
    withRootTemplate(
      bannerTemplate,
      BANNER_WRAPPER_HOOK,
      BANNER_WRAPPER_DATA_COMP_TYPE,
      rootClassName(fontsFallback, fontsLoading),
    ),
  )

  focusBannerOnRender()

  document.body.appendChild(root)

  const removeRoot = () => {
    root.parentElement?.removeChild(root)
    const siteContainer = document.getElementById("SCROLL_TO_TOP");
    if (useCipaBanner && dataCapsule) {
      // expires after 48 hours
      dataCapsule.setItem(CIPA_BANNER_CLOSED_FLAG, true, {
        expiration: 172800, category: 'essential'
      });
    }
    if (siteContainer) {
      siteContainer.focus({
        preventScroll: true
      });
    }
  }

  if (showDeclineAll) {
    root
      .querySelector(byHook(BANNER_DECLINE_ALL_HOOK))
      ?.addEventListener('click', () => {
        logger.declineAllClicked()
        consentPolicyManager.setConsentPolicy(WIX_EU_POLICY, noop, noop)
        removeRoot()
      })
  }

  root
    .querySelector(byHook(BANNER_ACCEPT_HOOK))
    ?.addEventListener('click', () => {
      logger.acceptButtonClicked()
      consentPolicyManager.setConsentPolicy(DEFAULT_POLICY, noop, noop)
      removeRoot()
    })

  root
    .querySelector(byHook(BANNER_DECLINE_HOOK))
    ?.addEventListener('click', () => {
      logger.dismissClicked()
      removeRoot()
    })

  root
    .querySelector(byHook(BANNER_HOOK))
    ?.addEventListener('keyup', (event: any) => {
      if (event.code !== 'Escape') {
        return
      }
      logger.dismissClicked()
      removeRoot()
    })

  root.querySelector(byHook(LINK_HOOK))?.addEventListener('click', () => {
    logger.cookieLinkClicked()
  })

  root
    .querySelector(byHook(BANNER_SETTINGS_HOOK))
    ?.addEventListener('click', () => {
      logger.openedPanel()
      const modal = render(
        withRootTemplate(
          createModalTemplate(t),
          MODAL_WRAPPER_HOOK,
          MODAL_WRAPPER_DATA_COMP_TYPE,
          rootClassName(fontsFallback, false),
        ),
      )
      root.appendChild(modal)
      const sections = modal.getElementsByTagName('section')
      if (sections.length > 0) {
        sections[0].focus()
      }

      const closeModalOnEsc = ({ code }: KeyboardEvent) => {
        if (code !== 'Escape') {
          return
        }

        removeModal()
      }

      const removeModal = () => {
        root.removeChild(modal)
        focusBanner()
        logger.closePanelClicked()
        document.removeEventListener('keyup', closeModalOnEsc)
      }

      document.addEventListener('keyup', closeModalOnEsc, { once: true })

      modal
        .querySelector(byHook(MODAL_SAVE_HOOK))
        ?.addEventListener('click', () => {
          const checkbox = modal.querySelector(
            byHook(MODAL_RECOMMENDED_CHECKBOX_HOOK),
          ) as HTMLInputElement

          const userAcceptedRecommendedCookies = checkbox.checked

          logger.saveButtonClicked(userAcceptedRecommendedCookies)

          consentPolicyManager.setConsentPolicy(
            userAcceptedRecommendedCookies ? DEFAULT_POLICY : WIX_EU_POLICY,
            noop,
            noop,
          )

          removeModal()
          removeRoot()
        })

      modal
        .querySelector(byHook(LINK_HOOK))
        ?.addEventListener('click', logger.cookieLinkClickedModal)

      modal
        .querySelector(byHook(MODAL_RECOMMENDED_CHECKBOX_HOOK))
        ?.addEventListener('click', () => {
          const checkbox = modal.querySelector(
            byHook(MODAL_RECOMMENDED_CHECKBOX_HOOK),
          ) as HTMLInputElement

          const toggle = checkbox.checked
          logger.toggleClicked(toggle)
        })

      modal
        .querySelector(byHook(MODAL_CLOSE_HOOK))
        ?.addEventListener('click', () => {
          removeModal()
        })

      modal
        .querySelector(byHook(MODAL_HOOK))
        ?.addEventListener('click', function (this: HTMLElement, e: Event) {
          if (e.target !== this) {
            return
          }

          removeModal()
        })
    })
}

const onFontLoaded = (fallback = false) => {
  fontsLoading = false
  const root = document.querySelector(byHook(BANNER_WRAPPER_HOOK))
  root?.classList.remove(classes.hidden)

  if (fallback) {
    fontsFallback = true
    root?.classList.add(classes.fontFallback)
  }
}

export const ConsentCookieBanner = (
  config: { showClose: boolean; showDeclineAll?: boolean } = {
    showClose: true,
  },
) => {
  if (!document.querySelector(byHook(BANNER_WRAPPER_HOOK))) {
    initTranslations(
      getStaticsUrl(),
      getLanguage(),
      (translate: TranslateFn) => {
        renderBanner(translate, config.showClose, config.showDeclineAll)
      },
    )
  }
}

ConsentCookieBanner.onFontsLoaded = onFontLoaded
